import React, { useState } from 'react'
import {
  AlertCircle, CheckCircle2, Loader2, Lightbulb,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import supportService, {
  SUGGESTION_CATEGORIES,
  IMPACT_AREAS,
} from '@/services/api/supportService'

interface SuggestionFormProps {
  onSuccess?: () => void
}

const SuggestionForm: React.FC<SuggestionFormProps> = ({ onSuccess }) => {
  const [category, setCategory] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [impactArea, setImpactArea] = useState('')
  const [businessValue, setBusinessValue] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedId, setSubmittedId] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!category || !title.trim() || !description.trim()) {
      setError('Please fill in all required fields')
      return
    }

    setIsSubmitting(true)
    try {
      const result = await supportService.createSuggestion({
        category,
        title: title.trim(),
        description: description.trim(),
        impact_area: impactArea || undefined,
        business_value: businessValue.trim() || undefined,
      })

      setSubmittedId(result.suggestion_id)
      setSubmitted(true)

      // Reset form
      setCategory('')
      setTitle('')
      setDescription('')
      setImpactArea('')
      setBusinessValue('')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to submit suggestion. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-4">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
          <Lightbulb className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Suggestion Submitted!</h3>
        <p className="text-sm text-gray-600">
          Your suggestion <Badge variant="outline" className="font-mono">{submittedId}</Badge> has been recorded.
          Our team will review it and get back to you.
        </p>
        <div className="flex gap-3 mt-2">
          <Button variant="outline" size="sm" onClick={() => setSubmitted(false)}>
            Submit Another
          </Button>
          <Button size="sm" onClick={onSuccess}>
            View My Submissions
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      {/* Intro */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <p className="font-medium flex items-center gap-1.5">
          <Lightbulb className="w-4 h-4" />
          Share your ideas
        </p>
        <p className="text-xs mt-1 text-amber-600">
          Your suggestions help us improve the platform for everyone at Eduvos.
        </p>
      </div>

      {/* Category */}
      <div className="space-y-1.5">
        <Label htmlFor="suggestion-category" className="text-sm font-medium">
          Category <span className="text-red-500">*</span>
        </Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger id="suggestion-category">
            <SelectValue placeholder="What type of suggestion?" />
          </SelectTrigger>
          <SelectContent>
            {SUGGESTION_CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div className="space-y-1.5">
        <Label htmlFor="suggestion-title" className="text-sm font-medium">
          Title <span className="text-red-500">*</span>
        </Label>
        <Input
          id="suggestion-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="A concise name for your suggestion"
          maxLength={500}
        />
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label htmlFor="suggestion-description" className="text-sm font-medium">
          Description <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="suggestion-description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe your suggestion in detail. What would you like to see? How would it work?"
          rows={4}
          className="resize-none"
        />
      </div>

      {/* Impact Area */}
      <div className="space-y-1.5">
        <Label htmlFor="suggestion-impact" className="text-sm font-medium">
          Impact Area
        </Label>
        <Select value={impactArea} onValueChange={setImpactArea}>
          <SelectTrigger id="suggestion-impact">
            <SelectValue placeholder="Which area would this improve?" />
          </SelectTrigger>
          <SelectContent>
            {IMPACT_AREAS.map(area => (
              <SelectItem key={area} value={area}>{area}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Business Value */}
      <div className="space-y-1.5">
        <Label htmlFor="suggestion-value" className="text-sm font-medium">
          Business Value <span className="text-gray-400 font-normal">(optional)</span>
        </Label>
        <Textarea
          id="suggestion-value"
          value={businessValue}
          onChange={e => setBusinessValue(e.target.value)}
          placeholder="Why does this matter? How would it help you or your team?"
          rows={2}
          className="resize-none"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Submit */}
      <Button
        type="submit"
        disabled={isSubmitting || !category || !title.trim() || !description.trim()}
        className="w-full bg-[#15305D] hover:bg-[#1a4a8a]"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit Suggestion'
        )}
      </Button>
    </form>
  )
}

export default SuggestionForm
